import { initializeApp, getApps, getApp, type FirebaseOptions } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getAnalytics, type Analytics } from "firebase/analytics";
import { getFunctions, httpsCallable, type Functions } from "firebase/functions";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject, uploadBytesResumable, type FirebaseStorage } from "firebase/storage";
import { PRIVACY_CONFIG } from "@/config/privacy";

// Firebase configuration from environment variables
const baseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const firebaseConfig: FirebaseOptions = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
  ? { ...baseConfig, measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID }
  : baseConfig;

// Validate configuration in development
if (import.meta.env.DEV) {
  const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'storageBucket'];
  const missingKeys = requiredKeys.filter(key => !firebaseConfig[key as keyof typeof firebaseConfig]);
  
  if (missingKeys.length > 0) {
    console.error('Missing required Firebase configuration:', missingKeys);
    console.error('Please check your .env.local file');
  }
}

// Initialize Firebase (avoid duplicate initialization)
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

// Log Firebase config in dev only
if (import.meta.env.DEV) {
  console.log('🔥 Firebase Configuration:', {
    environment: import.meta.env.VITE_ENVIRONMENT || 'development',
    projectId: firebaseConfig.projectId,
    authDomain: firebaseConfig.authDomain,
    storageBucket: firebaseConfig.storageBucket
  });
}

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);

// Make auth available globally for staging HTTP service only
if (typeof window !== 'undefined' && import.meta.env.VITE_ENVIRONMENT === 'staging') {
  (window as any).__firebaseAuth = auth;
}

// Only initialize analytics if privacy settings allow it
export let analytics: Analytics | null = null;
if (PRIVACY_CONFIG.ENABLE_ANALYTICS && typeof window !== 'undefined') {
  analytics = getAnalytics(app);
}

// Initialize Functions with standard Firebase SDK (same as production)
export let functions: Functions;
export { httpsCallable };

try {
  const region = (import.meta.env.VITE_FIREBASE_REGION as string) || 'us-central1';
  functions = getFunctions(app, region);
} catch (error: any) {
  console.error('❌ Firebase Functions initialization failed:', {
    name: error.name,
    message: error.message,
    code: error.code,
    stack: error.stack,
    environment: import.meta.env.VITE_ENVIRONMENT,
    region: (import.meta.env.VITE_FIREBASE_REGION as string) || 'us-central1'
  });
  throw error; // Let initialization failures bubble up instead of falling back
}

// Initialize Storage with static imports
export let storage: FirebaseStorage | null = null;
export { ref, uploadBytes, getDownloadURL, deleteObject, uploadBytesResumable };

try {
  storage = getStorage(app);
} catch (error: any) {
  console.error('Firebase Storage initialization failed:', error.message);
  storage = null;
}

// Backward compatibility function
export async function getStorageService() {
  return storage;
}

export default app;